import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Img } from '@gusto/workbench';
import { MagnifyingGlass } from 'components/pie/app_directory/constants/index';
import MagnifyingGlassPng from '../../../../images/illo-magnifying-glass-lg@2x.png';
import { trackSearch, trackCategoryClick, trackTileClick } from './analytics';
import CategoriesSidebar from './index/categories_sidebar';
import SearchBar from './index/search_bar';
import ListingTile from './index/listing_tile';
import Pager from './index/pager';
import { listing, categoryGroup } from './proptypes/index';
import { useDebounce } from './hooks/index';

const PAGE_SIZE = 20;
const paginateArray = (array, pageSize, pageNumber) => {
  return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
};

const AppDirectory = ({ listings, categoryGroups, selectedCategorySlug }) => {
  const [searchInput, setSearchInput] = useState('');
  const [timeoutAnalyticsFunction, setTimeoutAnalyticsFunction] = useState();
  const [selectedPage, setSelectedPage] = useState(1);
  const debouncedSearchInput = useDebounce(searchInput, 300, () => {
    setSelectedPage(1);
  });

  const selectedCategory = useMemo(() => {
    if (!selectedCategorySlug) return null;
    return categoryGroups.flatMap(g => g.categories).find(c => c.slug === selectedCategorySlug);
  }, [selectedCategorySlug, categoryGroups]);

  const filteredListings = useMemo(() => {
    if (debouncedSearchInput) {
      const re = new RegExp(debouncedSearchInput.replace(/[^\w\s]/gi, ''), 'gi');
      return listings.filter(
        l =>
          l.name.replace(/[^\w\s]/gi, '').match(re) ||
          l.categories.find(c => c.name.match(re)) ||
          l.short_description.match(re)
      );
    }
    if (selectedCategory) {
      return listings.filter(l => l.categories.some(c => c.slug === selectedCategory.slug));
    }
    return listings;
  }, [debouncedSearchInput, listings, selectedCategory]);

  const currentPageOfListings = useMemo(() => {
    return paginateArray(filteredListings, PAGE_SIZE, selectedPage);
  }, [selectedPage, filteredListings]);

  const titleDisplay = useMemo(() => {
    if (debouncedSearchInput) {
      return { title: `Search results (${filteredListings.length})`, description: '' };
    }
    if (selectedCategory) {
      return { title: selectedCategory.name, description: selectedCategory.description };
    }
    return {};
  }, [debouncedSearchInput, selectedCategory, filteredListings.length]);

  useEffect(() => {
    clearTimeout(timeoutAnalyticsFunction);
    setTimeoutAnalyticsFunction(
      setTimeout(() => {
        if (debouncedSearchInput) {
          trackSearch(debouncedSearchInput);
        }
      }, 1000)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  const onPageChange = pageNum => {
    setSelectedPage(pageNum);
    window?.scroll({ top: 0, left: 0 });
  };

  const onTileClick = appSlug => {
    trackTileClick(appSlug, selectedCategory, debouncedSearchInput);
  };

  return (
    <div className="appDirectory">
      {listings.length > 0 && (
        <div className="flex directoryContainer">
          <div className="sidebar">
            <CategoriesSidebar
              selectedSlug={selectedCategorySlug}
              categoryGroups={categoryGroups}
              onChange={trackCategoryClick}
            />
          </div>

          <div id="apps" className="apps">
            <SearchBar onChange={setSearchInput} value={searchInput} />

            {titleDisplay.title && (
              <div className="selectedCategory">
                <h4>{titleDisplay.title}</h4>
                <p>{titleDisplay.description}</p>
              </div>
            )}

            {filteredListings.length === 0 && (
              <div className="emptyState">
                <Img
                  alt={MagnifyingGlass.alt}
                  fit="content"
                  style={{ width: '5.6rem', height: 'auto', maxWidth: '50px' }}
                  height={50}
                  src={MagnifyingGlassPng}
                  width={50}
                />
                <h6>No results found</h6>
                <p>Try your search again using a different word or phrase.</p>
              </div>
            )}

            {filteredListings.length > 0 && (
              <div>
                <div className="tilesGrid">
                  {currentPageOfListings.map(l => (
                    <ListingTile key={l.slug} listing={l} onClick={onTileClick} />
                  ))}
                </div>

                <Pager
                  page={selectedPage}
                  totalResults={filteredListings.length}
                  resultsPerPage={PAGE_SIZE}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

AppDirectory.propTypes = {
  listings: PropTypes.arrayOf(listing).isRequired,
  categoryGroups: PropTypes.arrayOf(categoryGroup).isRequired,
  selectedCategorySlug: PropTypes.string,
};

AppDirectory.defaultProps = {
  selectedCategorySlug: null,
};

export default AppDirectory;
